import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import type {
  TourId,
  TourConfig,
  TourState,
  TourContextValue,
} from '../lib/tour/types';
import {
  tourConfigs as defaultTourConfigs,
  getTourConfig,
} from '../lib/tour/tours';
import {
  getTourStorage,
  markTourCompleted,
  markTourSkipped,
  resetTour as resetTourStorage,
  resetAllTours as resetAllToursStorage,
  getCompletedTours,
  getSkippedTours,
} from '../lib/tour/storage';

// Create context
const TourContext = createContext<TourContextValue | null>(null);

// Default styles for Joyride
const defaultStyles = {
  options: {
    primaryColor: '#10b981',
    textColor: '#1f2937',
    backgroundColor: '#ffffff',
    arrowColor: '#ffffff',
    overlayColor: 'rgba(0, 0, 0, 0.5)',
    spotlightShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
    zIndex: 10000,
  },
  tooltip: {
    borderRadius: 8,
    padding: 16,
  },
  tooltipContainer: {
    textAlign: 'left' as const,
  },
  tooltipTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 8,
  },
  tooltipContent: {
    fontSize: 14,
    lineHeight: 1.5,
  },
  buttonNext: {
    backgroundColor: '#10b981',
    borderRadius: 6,
    fontSize: 14,
    padding: '8px 16px',
  },
  buttonBack: {
    color: '#6b7280',
    marginRight: 8,
  },
  buttonSkip: {
    color: '#9ca3af',
  },
  buttonClose: {
    display: 'none',
  },
};

// Default Korean locale
const defaultLocale = {
  back: '이전',
  close: '닫기',
  last: '완료',
  next: '다음',
  skip: '건너뛰기',
  open: '가이드 열기',
};

interface TourProviderProps {
  children: React.ReactNode;
  /** Additional tour configs to merge with defaults */
  additionalTours?: TourConfig[];
  /** Override default styles */
  styles?: typeof defaultStyles;
  /** Override default locale */
  locale?: typeof defaultLocale;
  /** Disable auto-start for all tours */
  disableAutoStart?: boolean;
}

export function TourProvider({
  children,
  additionalTours = [],
  styles = defaultStyles,
  locale = defaultLocale,
  disableAutoStart = false,
}: TourProviderProps) {
  // Merge tour configs
  const allTourConfigs = useMemo(
    () => [...defaultTourConfigs, ...additionalTours],
    [additionalTours]
  );

  // Dynamic tour registry for runtime additions
  const [dynamicTours, setDynamicTours] = useState<TourConfig[]>([]);

  // All available tours
  const tours = useMemo(
    () => [...allTourConfigs, ...dynamicTours],
    [allTourConfigs, dynamicTours]
  );

  // Mounted state to ensure client-side only rendering for Joyride
  const [isMounted, setIsMounted] = useState(false);

  // Tour state
  const [state, setState] = useState<TourState>(() => ({
    activeTour: null,
    stepIndex: 0,
    isRunning: false,
    completedTours: getCompletedTours(),
    skippedTours: getSkippedTours(),
  }));

  // Mark as mounted after initial render
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Get current tour config
  const currentTour = useMemo(
    () => (state.activeTour ? tours.find((t) => t.id === state.activeTour) : null),
    [state.activeTour, tours]
  );

  // Get filtered steps (apply condition functions)
  const currentSteps = useMemo(() => {
    if (!currentTour) return [];
    return currentTour.steps.filter((step) => !step.condition || step.condition());
  }, [currentTour]);

  // Start a tour
  const startTour = useCallback((tourId: TourId) => {
    const tour = tours.find((t) => t.id === tourId);
    if (!tour || tour.steps.length === 0) {
      console.warn(`Tour "${tourId}" not found or has no steps`);
      return;
    }

    setState((prev) => ({
      ...prev,
      activeTour: tourId,
      stepIndex: 0,
      isRunning: true,
    }));
  }, [tours]);

  // Stop the current tour
  const stopTour = useCallback(() => {
    setState((prev) => ({
      ...prev,
      activeTour: null,
      stepIndex: 0,
      isRunning: false,
    }));
  }, []);

  // Go to next step
  const nextStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      stepIndex: prev.stepIndex + 1,
    }));
  }, []);

  // Go to previous step
  const prevStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      stepIndex: Math.max(0, prev.stepIndex - 1),
    }));
  }, []);

  // Skip the current tour
  const skipTour = useCallback(() => {
    if (state.activeTour) {
      markTourSkipped(state.activeTour);
      currentTour?.onSkip?.();
      setState((prev) => ({
        ...prev,
        activeTour: null,
        stepIndex: 0,
        isRunning: false,
        skippedTours: [...prev.skippedTours, state.activeTour!],
      }));
    }
  }, [state.activeTour, currentTour]);

  // Reset a specific tour
  const resetTour = useCallback((tourId: TourId) => {
    resetTourStorage(tourId);
    setState((prev) => ({
      ...prev,
      completedTours: prev.completedTours.filter((id) => id !== tourId),
      skippedTours: prev.skippedTours.filter((id) => id !== tourId),
    }));
  }, []);

  // Reset all tours
  const resetAllTours = useCallback(() => {
    resetAllToursStorage();
    setState((prev) => ({
      ...prev,
      completedTours: [],
      skippedTours: [],
    }));
  }, []);

  // Check if a tour is completed
  const isTourCompleted = useCallback(
    (tourId: TourId) => state.completedTours.includes(tourId),
    [state.completedTours]
  );

  // Check if a tour is available
  const isTourAvailable = useCallback(
    (tourId: TourId) => {
      const tour = tours.find((t) => t.id === tourId);
      if (!tour || tour.steps.length === 0) return false;

      // Check if first step target exists
      const firstStep = tour.steps[0];
      if (firstStep) {
        const element = document.querySelector(firstStep.target);
        return !!element;
      }
      return false;
    },
    [tours]
  );

  // Get available tours
  const getAvailableTours = useCallback(
    () => tours.filter((t) => t.steps.length > 0),
    [tours]
  );

  // Register a new tour dynamically
  const registerTour = useCallback((config: TourConfig) => {
    setDynamicTours((prev) => {
      const exists = prev.some((t) => t.id === config.id);
      if (exists) {
        return prev.map((t) => (t.id === config.id ? config : t));
      }
      return [...prev, config];
    });
  }, []);

  // Unregister a tour
  const unregisterTour = useCallback((tourId: TourId) => {
    setDynamicTours((prev) => prev.filter((t) => t.id !== tourId));
  }, []);

  // Handle Joyride callback
  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, action, index, type } = data;

      // Handle tour completion
      if (status === STATUS.FINISHED) {
        if (state.activeTour) {
          markTourCompleted(state.activeTour);
          currentTour?.onComplete?.();
          setState((prev) => ({
            ...prev,
            activeTour: null,
            stepIndex: 0,
            isRunning: false,
            completedTours: [...prev.completedTours, state.activeTour!],
          }));
        }
      }

      // Handle skip
      if (status === STATUS.SKIPPED) {
        skipTour();
      }

      // Handle close
      if (action === ACTIONS.CLOSE) {
        stopTour();
      }

      // Handle step change
      if (type === EVENTS.STEP_AFTER) {
        const direction = action === ACTIONS.PREV ? -1 : 1;
        setState((prev) => ({
          ...prev,
          stepIndex: prev.stepIndex + direction,
        }));

        // Call step callbacks
        const currentStep = currentSteps[index];
        if (currentStep?.onHide) {
          currentStep.onHide();
        }

        const nextStepIndex = index + direction;
        const nextStep = currentSteps[nextStepIndex];
        if (nextStep?.onShow) {
          nextStep.onShow();
        }
      }

      // Handle step shown
      if (type === EVENTS.TOOLTIP) {
        const step = currentSteps[index];
        if (step?.onShow) {
          step.onShow();
        }
      }
    },
    [state.activeTour, currentTour, currentSteps, skipTour, stopTour]
  );

  // Auto-start onboarding for first-time users
  useEffect(() => {
    if (disableAutoStart) return;

    // Wait for DOM to be ready
    const timer = setTimeout(() => {
      const storage = getTourStorage();

      // Find tours that should auto-start
      for (const tour of tours) {
        if (
          tour.autoStart &&
          !storage.completed.includes(tour.id) &&
          !storage.skipped.includes(tour.id) &&
          (!tour.autoStartCondition || tour.autoStartCondition())
        ) {
          // Check if the first step target exists
          const firstStep = tour.steps[0];
          if (firstStep) {
            const element = document.querySelector(firstStep.target);
            if (element) {
              startTour(tour.id);
              break; // Only start one auto-tour at a time
            }
          }
        }
      }
    }, 1000); // Delay to ensure DOM is rendered

    return () => clearTimeout(timer);
  }, [disableAutoStart, tours, startTour]);

  // Context value
  const contextValue: TourContextValue = {
    state,
    startTour,
    stopTour,
    nextStep,
    prevStep,
    skipTour,
    resetTour,
    resetAllTours,
    isTourCompleted,
    isTourAvailable,
    getAvailableTours,
    registerTour,
    unregisterTour,
  };

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      {isMounted && (
        <Joyride
          steps={currentSteps}
          stepIndex={state.stepIndex}
          run={state.isRunning}
          continuous
          showProgress
          showSkipButton
          scrollToFirstStep
          disableScrolling={false}
          disableOverlayClose
          spotlightPadding={8}
          callback={handleJoyrideCallback}
          styles={styles}
          locale={locale}
          floaterProps={{
            disableAnimation: false,
          }}
        />
      )}
    </TourContext.Provider>
  );
}

/**
 * Hook to access tour context
 */
export function useTour(): TourContextValue {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}

/**
 * Hook to check if a specific tour is completed
 */
export function useTourCompleted(tourId: TourId): boolean {
  const { isTourCompleted } = useTour();
  return isTourCompleted(tourId);
}

/**
 * Hook to start a tour programmatically
 */
export function useStartTour() {
  const { startTour } = useTour();
  return startTour;
}
